import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxIconName, OnyxOptional } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxDataWidgetComponent,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxLinkComponent,
  OnyxMessageComponent,
  OnyxSpinnerComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { DelegationsRoute } from '../../../../dashboard/delegations/common/enums/delegations-route';
import { DelegationHelper } from '../../../../dashboard/delegations/common/helpers/delegation.helper';
import { FleetDescriptionParametersComponent } from '../../../../dashboard/fleet/common/components/fleet-description-parameters/fleet-description-parameters.component';
import { FleetCategory } from '../../../../dashboard/fleet/common/enums/fleet-category';
import { FleetHelper } from '../../../../dashboard/fleet/common/helpers/fleet.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../dashboard/fleet/common/interfaces/fleet';
import { OrderPointStatus } from '../../../../dashboard/orders/common/enums/order-point-status';
import { OrderHelper } from '../../../../dashboard/orders/common/helpers/order.helper';
import { DictionaryCode } from '../../../enums/dictionary-code';
import { RouteHelper } from '../../../helpers/route.helper';
import { FleetIdentifierPipe } from '../../../pipes/fleet-identifier.pipe';
import { TelematicsStore } from '../../../stores/telematics.store';
import { NavigationHelper } from '../../../helpers/navigation.helper';

@Component({
  selector: 'app-modal-vehicles',
  imports: [
    OnyxDataWidgetComponent,
    OnyxIconComponent,
    OnyxButtonComponent,
    TranslatePipe,
    NgTemplateOutlet,
    FleetDescriptionParametersComponent,
    OnyxTooltipDirective,
    FleetIdentifierPipe,
    OnyxLinkComponent,
    OnyxIconButtonComponent,
    OnyxSpinnerComponent,
    OnyxMessageComponent,
    OnyxInformationHeadingComponent,
  ],
  templateUrl: './modal-vehicles.component.html',
  styleUrl: './modal-vehicles.component.scss',
})
export class ModalVehiclesComponent {
  protected readonly telematicsStore = inject(TelematicsStore);
  private readonly router = inject(Router);
  private readonly orderHelper = inject(OrderHelper);
  private readonly fleetHelper = inject(FleetHelper);

  public readonly vehicle =
    input<OnyxOptional<Fleet | SimplifiedFleet | null>>();
  public readonly trailer =
    input<OnyxOptional<Fleet | SimplifiedFleet | null>>();
  public readonly uuid = input.required<string | null>();
  public readonly close$ = input.required<Subject<void>>();
  public readonly showEdit = input(false);

  public readonly edit = output<void>();

  protected readonly I18N = 'modals';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly FleetCategory = FleetCategory;
  protected readonly FleetHelper = FleetHelper;

  protected hasActiveDelegation = computed(() => {
    const vehicle = this.vehicle();
    if (vehicle && !FleetHelper.isFleetType(vehicle)) return false;

    const delegation = vehicle?.delegation;
    if (!delegation) return false;

    return DelegationHelper.isActive(delegation);
  });
  protected loadedSemiTrailer = computed(() => {
    const data = this.pickupPointTelematicsData();
    if (!data?.order || this.trailer()) return null;

    const { basicInformation, uuid, identifier } = data.order;
    const { registrationNumber } = basicInformation.loadedSemiTrailer;

    if (
      registrationNumber &&
      data.point.status === OrderPointStatus.COMPLETED
    ) {
      return {
        registrationNumber,
        minSemiTrailerSize: basicInformation.minSemiTrailerSize,
        vehicleType: basicInformation.vehicleTypes[0],
      };
    }

    return {
      type: registrationNumber ? 'outsourcing' : 'internal',
      orderUuid: uuid,
      orderIdentifier: identifier,
    };
  });

  private telematicsItem = computed(() =>
    this.telematicsStore.getItem(this.vehicle()?.uuid),
  );

  private pickupPointTelematicsData = computed(() => {
    const routePoints = this.telematicsItem()?.routePoints ?? [];
    const routePointMap = new Map(
      routePoints.map((routePoint) => [
        RouteHelper.getUuid(routePoint),
        routePoint,
      ]),
    );

    return routePoints.find((routePoint) => {
      if (!routePoint.order) return false;

      const { dropPointUuid, pickupPointUuid } =
        routePoint.order.basicInformation.loadedSemiTrailer;
      if (routePoint.point.uuid !== pickupPointUuid) return false;

      const dropPoint = dropPointUuid ? routePointMap.get(dropPointUuid) : null;

      return !dropPoint || !RouteHelper.isCompleted(dropPoint);
    });
  });

  protected editDelegation(): void {
    if (!this.uuid()) return;

    this.router.navigateByUrl(
      DelegationsRoute.EDIT_DELEGATION.replace(':uuid', this.uuid()!),
      NavigationHelper.withOrigin(this.router),
    );
    this.close$().next();
  }

  protected openFleetModal(fleet: SimplifiedFleet): void {
    this.fleetHelper.openModal(fleet);
  }

  protected openOrderModal(orderUuid: string): void {
    this.orderHelper.openModal(orderUuid);
  }

  protected getFleetIcon(category: FleetCategory): OnyxIconName {
    return `${category}-filled`;
  }
}
